import React from "react";
import { useLenis } from "@/hooks/useLenis";
import { useShopifyProduct } from "@/hooks/useShopifyProduct";
import { useSeo } from "@/lib/seo";
import { graph, webPageLd, productLd, faqLd } from "@/lib/schema";
import { reviewsFor, REVIEW_COLORS } from "@/lib/reviews";
import { Footer } from "@/components/site/Footer";
import { FloatingWhatsApp } from "@/components/site/FloatingWhatsApp";
import { Breadcrumbs, breadcrumbJsonLd } from "@/components/site/Breadcrumbs";
import { LandingHero } from "@/components/landing/LandingHero";
import { TrustStrip, Manifesto, PromiseSection } from "@/components/landing/Sections";
import { ProcessTimeline } from "@/components/landing/ProcessTimeline";
import { ProductShowcase } from "@/components/landing/ProductShowcase";
import { WaysToEnjoy, LandingReviews, LandingFAQ, FinalCTA, StickyBuyBar } from "@/components/landing/Extras";

export default function ProductLanding({ config }) {
  useLenis();
  const shop = useShopifyProduct(config.shopifyTitle, config.fallbackPrice);
  const crumbs = [{ label: "Home", to: "/" }, { label: "Products", to: "/#products" }, { label: config.shortName }];

  useSeo({
    title: config.seo.title,
    description: config.seo.description,
    path: config.path,
    type: "product",
    image: shop.product?.featuredImage?.url || config.hero.image,
    jsonLd: graph(
      webPageLd({ path: config.path, name: config.seo.title, description: config.seo.description, type: "ItemPage" }),
      productLd({ path: config.path, product: shop.product, fallbackTitle: config.shopifyTitle, fallbackPrice: config.fallbackPrice, description: config.seo.description, image: config.hero.image }),
      breadcrumbJsonLd(crumbs),
      faqLd(config.faqs),
    ),
  });

  return (
    <>
      <main data-testid={`landing-page-${config.key}`}>
        <LandingHero config={config} shop={shop} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 -mt-6 mb-4 relative z-10">
          <Breadcrumbs items={crumbs} />
        </div>
        <TrustStrip badges={config.badges} />
        <ProductShowcase config={config} shop={shop} />
        <Manifesto data={config.manifesto} sideImage={config.sideImage} sideAlt={`Rudrima ${config.shortName} ${config.weight} — ${config.key === "ghee" ? "traditional bilona made cow ghee served with rotis" : "raw forest honey in a glass jar"}`} />
        <ProcessTimeline data={config.process} />
        {config.ways && <WaysToEnjoy data={config.ways} />}
        <PromiseSection data={config.promise} />
        <LandingReviews reviews={reviewsFor(config.key)} colors={REVIEW_COLORS} />
        <LandingFAQ faqs={config.faqs} productName={config.shortName} />
        <FinalCTA data={config.finalCta} shop={shop} />
      </main>
      <Footer />
      <FloatingWhatsApp />
      <StickyBuyBar config={config} shop={shop} />
    </>
  );
}
